import {
  Blocks,
  FlaskConical,
  Layers,
  Palette,
  PenLine,
  Plug,
  Rocket,
  Search,
  Wrench,
} from 'lucide-react'

export const agencyServices = [
  {
    title: 'Management',
    description:
      'Hands-on stewardship of websites, databases, hosting, and backups — organised, monitored, and documented so your stack stays recoverable and ready when you need to move.',
    Icon: Layers,
  },
  {
    title: 'Design',
    description:
      'Website design that balances brand, clarity, and conversion — from structure and responsive layouts to the details that make every visit feel intentional.',
    Icon: Palette,
  },
  {
    title: 'Maintenance',
    description:
      'Updates, security hardening, and performance care for your site and its software, plus responsive support when something breaks or your team needs a steady hand.',
    Icon: Wrench,
  },
  {
    title: 'Integration',
    description:
      'Reliable connections to third-party CRMs, APIs, and the tools you already use — so data flows between your site, your systems, and your people without brittle workarounds.',
    Icon: Plug,
  },
] as const

export const servicesGridCategories = [
  {
    title: 'Projects &\nDelivery',
    items: [
      'End-to-end project management',
      'Multi-stakeholder coordination',
      'Scoping and phased delivery',
      'Timelines and milestones',
      'Plain-language reporting',
    ],
  },
  {
    title: 'Design &\nDevelopment',
    items: [
      'Responsive from mobile to desktop',
      'Design systems and UI kits',
      'Conversion-focused UX',
      'Web application development',
      'Accessibility built in',
    ],
  },
  {
    title: 'Support &\nMaintenance',
    items: [
      'Ongoing care and maintenance',
      'Same-day turnaround',
      'CMS and end-user training',
      'Security patching and updates',
      'Performance monitoring',
      'Incident response',
    ],
  },
  {
    title: 'Integrations &\nAPIs',
    items: [
      'CRM and ERP integrations',
      'Custom API development',
      'Payments and checkout flows',
      'Marketing automation wiring',
      'Real-time data sync',
    ],
  },
  {
    title: 'Search &\nPerformance',
    items: [
      'Technical SEO foundations',
      'High-converting landing pages',
      'Analytics and reporting',
      'PageSpeed optimisation',
      'AI and LLM visibility',
      'Social reach and engagement',
    ],
  },
  {
    title: 'Hosting &\nMigrations',
    items: [
      'Zero-downtime migrations',
      'Server architecture and scaling',
      'Automated deployment pipelines',
      'High-availability hosting',
      'Backups and disaster recovery',
    ],
  },
] as const

export const servicesGridQuotes = [
  {
    quote:
      'Great project management starts with real human connection. I love getting to know our clients, keeping communication effortless, and making sure every complex build runs smoothly to a seamless launch.',
    name: 'Christie Connelly',
    role: 'Director and Project Manager',
  },
  {
    quote:
      "The best websites aren't just built to launch, they're built to evolve!\nCreating scalable, maintainable solutions that support our clients for years is what gives me the most satisfaction.",
    name: 'Dillon Connelly',
    role: 'Director and Lead Developer',
  },
] as const

export const aboutUsBannerContent = {
  label: 'About us',
  heading: 'People, pace, and partners we grow with',
  imageSrc: '/about-us-banner.png',
  secondaryImageSrc: '/hero-ether-clouds-landscape-2400.webp',
  details: [
    {
      title: 'North Coast roots',
      description:
        'Morning Studio has been on the North Coast of NSW since 2007 — building websites, running campaigns, and writing copy for organisations across Australia and overseas.',
    },
    {
      title: 'Clear delivery',
      description:
        'A small team with clear communication and delivery you can plan around — no ticket treadmill, no chasing status in threads.',
    },
  ],
} as const

export const processBannerContent = {
  label: 'Process',
  heading: 'From brief to launch, with fewer surprises',
  imageSrc: '/about-us-banner.png',
  details: [
    {
      title: 'Discover',
      description:
        'We start with your goals, audiences, and constraints — then define site structure, content priorities, and the metrics that will define success before design begins.',
    },
    {
      title: 'Launch',
      description:
        'We go live, validate analytics and technical SEO foundations, and hand over clear documentation — so you are confident managing what comes next.',
    },
  ],
} as const

export const howWeWorkSteps = [
  {
    title: 'Discover',
    description:
      'We start with your goals, audiences, and constraints — then define site structure, content priorities, and the metrics that will define success before design begins.',
    Icon: Search,
  },
  {
    title: 'Design',
    description:
      'Wireframes and prototypes become a coherent visual system: responsive layouts, accessible UI, and copy tuned for real people and conversion.',
    Icon: PenLine,
  },
  {
    title: 'Develop',
    description:
      'We implement production-ready code with clean markup, fast assets, and your CMS, ecommerce stack, or integrations wired in the way your team can maintain.',
    Icon: Blocks,
  },
  {
    title: 'Test',
    description:
      'We validate flows, accessibility, and performance against the success metrics agreed in discovery — catching issues before they reach your audience.',
    Icon: FlaskConical,
  },
  {
    title: 'Launch',
    description:
      'We go live, validate analytics and technical SEO foundations, and hand over clear documentation — so you are confident managing what comes next.',
    Icon: Rocket,
  },
] as const

export const processCarouselSteps = [
  {
    title: 'Begin with a clear brief',
    titleEmphasis: 'and agreed scope',
    description:
      'We start with your goals, audiences, and constraints — then define structure, content priorities, and success metrics before design begins.',
  },
  {
    title: 'Continue iteration',
    titleEmphasis: 'without friction',
    description:
      'Wireframes become a visual system, then production-ready code — with milestones you can plan around and updates that stay human.',
  },
  {
    title: 'Stay confident after',
    titleEmphasis: 'launch day',
    description:
      'We go live, validate analytics and technical SEO foundations, and hand over documentation your team can extend with confidence.',
  },
] as const

export type FaqAnswerBlock =
  | { type: 'paragraph'; text: string }
  | {
      type: 'list'
      items: readonly { title: string; text: string }[]
    }

export const faqItems = [
  {
    question: 'Who is Morning Studio?',
    answer: [
      {
        type: 'paragraph',
        text: 'Morning Studio is a digital development agency established in 2007 by partners Dillon and Christie. Based in the Northern Rivers of NSW, we’re proudly 100% Australian owned and operated, with all strategy, project management, and core development managed onshore.',
      },
      {
        type: 'paragraph',
        text: 'Over the past two decades, we’ve become a trusted technical partner for leading creative agencies and commercial brands across Australia and internationally. We’re known for transforming ambitious designs into pixel-perfect, high-performance digital platforms that are secure, scalable, and built to support long-term growth.',
      },
    ],
  },
  {
    question: 'What does Morning Studio do?',
    answer: [
      {
        type: 'paragraph',
        text: 'We design, build, and support custom websites and applications for businesses, agencies, and organisations that need more than an off-the-shelf solution.',
      },
      {
        type: 'paragraph',
        text: 'Our expertise spans enterprise WordPress and WooCommerce development, Shopify and Shopify Plus, custom web applications, systems integration, and long-term website management.',
      },
      {
        type: 'paragraph',
        text: 'Whether we’re working directly with a brand or as the white-label development team behind a creative agency, our focus is always on delivering reliable, high-quality technical solutions.',
      },
    ],
  },
  {
    question: 'What does the average project timeline look like?',
    answer: [
      {
        type: 'paragraph',
        text: 'Every project follows a structured development process that takes it from discovery through to launch. This includes technical planning, design, custom development, staging, testing, and deployment, with dedicated feedback rounds throughout the build.',
      },
      {
        type: 'paragraph',
        text: 'Most projects are completed within three to five months, depending on complexity, integrations, and content requirements.',
      },
      {
        type: 'paragraph',
        text: 'Our project management process keeps clients informed at every stage with clear milestones and predictable delivery.',
      },
    ],
  },
  {
    question: 'Do you offer white-label services for agencies?',
    answer: [
      {
        type: 'paragraph',
        text: 'Absolutely. White-label development has been a core part of Morning Studio since the beginning.',
      },
      {
        type: 'paragraph',
        text: 'We work as an invisible extension of creative, marketing, and branding agencies, delivering custom development under strict NDA agreements. This allows our agency partners to confidently take on larger or more complex digital projects while we handle the technical delivery behind the scenes.',
      },
    ],
  },
] as const satisfies readonly {
  question: string
  answer: readonly FaqAnswerBlock[]
}[]

export const designProcessSectionCopy = {
  eyebrow: 'Process',
  heading: 'From brief through support,',
  headingEmphasis: 'with fewer surprises',
  subtitle:
    'Discover, design, develop, test, launch, and support — with clear milestones and updates you can plan around.',
} as const

export const designProcessSteps = [
  {
    id: 'discover',
    title: 'Discover',
    description:
      'We start with your goals, audiences, and constraints — then define site structure, content priorities, and the metrics that will define success before design begins.',
  },
  {
    id: 'design',
    title: 'Design',
    description:
      'Wireframes and prototypes become a coherent visual system: responsive layouts, accessible UI, and copy tuned for real people and conversion.',
  },
  {
    id: 'develop',
    title: 'Develop',
    description:
      'We implement production-ready code with clean markup, fast assets, and your CMS, ecommerce stack, or integrations wired in the way your team can maintain.',
  },
  {
    id: 'test',
    title: 'Test',
    description:
      'We validate flows, accessibility, and performance against the success metrics agreed in discovery — catching issues before they reach your audience.',
  },
  {
    id: 'launch',
    title: 'Launch',
    description:
      'We go live, validate analytics and technical SEO foundations, and hand over clear documentation — so you are confident managing what comes next.',
  },
  {
    id: 'support',
    title: 'Support',
    description:
      'We provide proactive maintenance, off-site backups, and priority bug fixes — backed by flexible dev hours and dedicated technical oversight to keep your website or application fast, secure, and evolving long after launch day.',
  },
] as const

export const faqSectionCopy = {
  eyebrow: 'Frequently Asked Questions',
  heading: 'Everything you want to know',
  subtitle:
    "Answers to the questions we're asked most by agencies, marketing teams and growing businesses.",
} as const

export const faqCaptionSectionCopy = {
  eyebrow: 'Caption section',
  heading: 'Placeholder headline goes here',
  subtitle:
    'Placeholder description for this section. Replace with final copy when ready.',
  caption:
    'Caption text goes here. Replace with final copy when ready — a supporting paragraph that sits where the FAQ questions list would be.',
} as const

export const faqCaptionReversedSectionCopy = {
  eyebrow: 'Caption section',
  heading: 'Placeholder headline goes here',
  subtitle:
    'Placeholder description for this section. Replace with final copy when ready.',
  caption:
    'Caption text goes here. Replace with final copy when ready — reversed layout with the image on the left and caption on the right.',
} as const

export const statsSectionCopy = {
  eyebrow: 'Statistics',
  heading: 'Trust Earned Through Performance',
  subtitle:
    'We serve as a vital Technical Partner for leading creative agencies and a trusted Solutions Architect for global commercial entities.',
} as const

export const contactSectionCopy = {
  eyebrow: 'Contact',
  heading: "Tell us what you're building",
  subtitle:
    'Quick hello or full brief — we read everything and reply with something useful.',
  imageSrc: '/contact-lennox.png',
  imageSrcDark: '/contact-lennox-dark.png',
  imageAlt: 'Driftwood on Lennox Head beach',
} as const

export const statsItems = [
  {
    value: '20',
    label: 'years building, managing and maintaining for the web',
  },
  {
    value: '100%',
    label: 'Australian-based web development & project management',
  },
  {
    value: '200+',
    label: 'websites and applications under management',
  },
  {
    value: '99.9%',
    label:
      'Uptime for core system availability & hosting across managed platforms',
  },
] as const

export const testimonials = [
  {
    quote:
      'The new site is fast, clear, and finally matches how we pitch in the room — leads understand what we do in seconds.',
    name: 'Marketing lead',
    company: 'Professional services',
    avatar: 'https://assets.ui.sh/avatars/3.webp?size=112',
  },
  {
    quote:
      'Search and paid campaigns are now reported in plain language. We see what changed and why, not just a wall of metrics.',
    name: 'Operations manager',
    company: 'Regional business',
    avatar: 'https://assets.ui.sh/avatars/7.webp?size=112',
  },
  {
    quote:
      'Ongoing support means small fixes and updates do not turn into internal fire drills — we send a note and it gets handled.',
    name: 'Studio director',
    company: 'Creative industry',
    avatar: 'https://assets.ui.sh/avatars/11.webp?size=112',
  },
  {
    quote:
      'Copy and email journeys finally sound like us. Engagement went up without us chasing trends.',
    name: 'Founder',
    company: 'Ecommerce brand',
    avatar: 'https://assets.ui.sh/avatars/14.webp?size=112',
  },
  {
    quote:
      'We shipped on time without cutting corners on accessibility or performance — the handoff was documented and questions get real answers.',
    name: 'Product owner',
    company: 'SaaS startup',
    avatar: 'https://assets.ui.sh/avatars/5.webp?size=112',
  },
  {
    quote:
      'They listen first, then recommend — no cookie-cutter packages. Milestones were things we could show stakeholders.',
    name: 'Program director',
    company: 'Non-profit',
    avatar: 'https://assets.ui.sh/avatars/2.webp?size=112',
  },
  {
    quote:
      'Our WordPress migration landed without downtime scares. Redirects, forms, and analytics were checked before anyone outside the team saw the new site.',
    name: 'Digital manager',
    company: 'Membership organisation',
    avatar: 'https://assets.ui.sh/avatars/9.webp?size=112',
  },
  {
    quote:
      'Retained support turned our backlog into a shared queue with clear priorities — we finally stopped debating what should ship next.',
    name: 'Head of marketing',
    company: 'Hospitality group',
    avatar: 'https://assets.ui.sh/avatars/12.webp?size=112',
  },
  {
    quote:
      'The CRM integration saved hours every week. Leads flow in cleanly and the sales team trusts the data again.',
    name: 'Revenue lead',
    company: 'B2B services',
    avatar: 'https://assets.ui.sh/avatars/16.webp?size=112',
  },
  {
    quote:
      'Design and build felt like one conversation, not two vendors. The site reflects our brand without us micromanaging every pixel.',
    name: 'Brand manager',
    company: 'Consumer goods',
    avatar: 'https://assets.ui.sh/avatars/15.webp?size=112',
  },
] as const

export const featuredTestimonials = testimonials

export const testimonialsSectionCopy = {
  heading: 'The standard',
  headingEmphasis: 'for lasting partnerships',
  subheading: 'Web work for teams that expect clarity, pace, and craft',
} as const

/**
 * Landing page testimonials, shown in a masonry grid. Each quote goes to the
 * column holding the least text so far, so order sets roughly where it lands.
 */
export const landingTestimonialsCopy = {
  eyebrow: 'Testimonials',
  heading: 'Built with us, and still with us',
  subtitle: 'Clients we’ve launched for, and kept looking after since.',
} as const

type LandingTestimonial = {
  quote: string
  name: string
  role?: string
  company: string
  imageSrc?: string
}

export const landingTestimonials: readonly LandingTestimonial[] = [
  {
    quote:
      'Morning Studio designed and built our website from scratch, collaborating seamlessly across all our internal teams—from marketing and sales to design and admin—so everyone felt informed and aligned. We feel completely looked after on our support retainer; their online project management tool makes logging tickets and tracking progress effortless. Best of all, they’re genuine, approachable, and always a great time to hang out with in meetings!',
    name: 'Tom Arciuli',
    company: 'Ricky Richards',
  },
  {
    quote:
      'Morning Studio has been a valued partner to WHSmith Australia, supporting us with website management, technical enhancements, and ongoing digital projects. Their team consistently delivers responsive service, practical solutions, and expert guidance. Professional, reliable, and easy to work with, they have played an important role in our digital success.',
    name: 'Christian Ivers',
    company: 'WHSmith',
  },
  {
    quote:
      'We’ve had a fantastic experience working with the Morning team. They brought our new website to life with professionalism and care, and their ongoing support has been outstanding. They’re always prompt, approachable, and incredibly easy to work with.',
    name: 'Michael Potas',
    company: 'Boost Design',
  },
  {
    quote:
      'Morning Studio designed and built our website from the ground up, and they’ve been taking care of us ever since. Having them on an ongoing support retainer gives us total peace of mind—whenever we need a site tweak or have a support ticket, their turnaround time is insanely fast. Dealing with people who run and understand the SME world is a breath of fresh air.',
    name: 'Trish Cashmere',
    company: 'THBC',
    imageSrc: '/images/testimonials/trish-cashmere-healthy-body-company.webp',
  },
  {
    quote:
      'As a designer, handoff is usually the most nerve-wracking part of a project, but Morning Studio changes that completely. They build our designs pixel-perfect and fully responsive across every single viewport, every single time. I love working with them because my vision comes to life exactly as intended without any compromises.',
    name: 'Mike Gordon',
    company: 'Folio Creative',
  },
  {
    quote:
      'From streamlining our backend process to custom web app engineering, Morning Studio transformed how we run Gecco Blinds. Their technical execution didn’t just save us time—it helped us earn over 250 5-star Google reviews.',
    name: 'Chad Mail',
    company: 'Gecco Blinds',
    imageSrc: '/images/testimonials/chad-mail-gecco-blinds.webp',
  },
]

export const testimonialCardTilts = [
  '-rotate-2',
  'rotate-1',
  '-rotate-1',
  'rotate-2',
  '-rotate-1',
  'rotate-1',
] as const

export const quoteHangClass =
  "relative before:absolute before:inline before:-translate-x-full before:content-['\\201C'] after:inline after:content-['\\201D']"

export const footerTagline =
  'Stay up to date with the latest from the studio'

export const footerAcknowledgementOfCountry = {
  lead:
    'We acknowledge the Bundjalung Nation as the Traditional Custodians of the land, sea and waterways of our area.',
  respect: 'We pay our respects to Elders past and present.',
} as const

export const footerLinks = [
  { label: 'Services', href: '/#services' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Contact', href: '/#contact' },
  { label: 'Blog', href: '/blog' },
  { label: 'Privacy', href: '/privacy-policy' },
  { label: 'Terms', href: '/terms' },
] as const

export type FooterNavLink = {
  label: string
  href: string
  external?: boolean
}

export type FooterNavGroup = {
  id: string
  title: string
  links: readonly FooterNavLink[]
  ctas?: readonly {
    label: string
    href: string
    variant: 'filled' | 'outlined'
  }[]
}

export const footerNavGroups: readonly FooterNavGroup[] = [
  {
    id: 'footer-sitemap',
    title: 'Sitemap',
    links: [
      { label: 'Home', href: '/#top' },
      { label: 'Services', href: '/#services' },
      { label: 'Process', href: '/#design-process' },
      { label: 'FAQ', href: '/#faq' },
      { label: 'Contact', href: '/#contact' },
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms & Conditions', href: '/terms' },
    ],
  },
  {
    id: 'footer-services',
    title: 'Services',
    links: [
      { label: 'Management', href: '/#services' },
      { label: 'Design', href: '/#services' },
      { label: 'Maintenance', href: '/#services' },
      { label: 'Integration', href: '/#services' },
      { label: 'Search & performance', href: '/#services' },
    ],
  },
  {
    id: 'footer-connect',
    title: 'Connect',
    links: [
      {
        label: 'Email',
        href: 'mailto:hello@morning.am',
        external: true,
      },
      {
        label: 'LinkedIn',
        href: 'https://au.linkedin.com/company/morningstudio-au',
        external: true,
      },
      {
        label: 'Instagram',
        href: 'https://www.instagram.com/morningstudio_au',
        external: true,
      },
      { label: 'Blog', href: '/blog' },
    ],
    ctas: [
      { label: 'Talk to us', href: '/#contact', variant: 'filled' },
      { label: 'Get started', href: '/#contact', variant: 'outlined' },
    ],
  },
] as const

export const footerSocialLinks = [
  {
    label: 'LinkedIn',
    href: 'https://au.linkedin.com/company/morningstudio-au',
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/morningstudio_au',
  },
] as const

export const contactChannels = {
  email: {
    href: 'mailto:hello@morning.am',
    display: 'hello@morning.am',
  },
  phone: {
    href: 'tel:1300559343',
    display: '(+61) 1300 559 343',
  },
  snailMail: {
    display: 'PO BOX 80, Alstonville NSW 2477',
  },
} as const
